import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity,
  BarChart3,
  Ban,
  CheckCircle2,
  Edit3,
  ExternalLink,
  KeyRound,
  Loader2,
  Lock,
  LogOut,
  MessageSquare,
  Search,
  Send,
  Settings,
  Shield,
  ShoppingBag,
  Ticket,
  Users,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "berlin-roleplay-admin-token";

function getToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY) || "";
}

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function adminGet(path) {
  const response = await axios.get(`${API}${path}`, { headers: authHeaders() });
  return response.data.data;
}

async function adminPost(path, body = {}) {
  const response = await axios.post(`${API}${path}`, body, { headers: authHeaders() });
  return response.data.data ?? response.data;
}

async function adminPatch(path, body = {}) {
  const response = await axios.patch(`${API}${path}`, body, { headers: authHeaders() });
  return response.data.data ?? response.data;
}

async function adminDelete(path) {
  const response = await axios.delete(`${API}${path}`, { headers: authHeaders() });
  return response.data;
}

function normalizeError(error) {
  return error?.response?.data?.detail || error?.response?.data?.error || error?.message || "Unbekannter Fehler";
}

export function trackEvent(path, body = {}) {
  const session = localStorage.getItem("berlin-roleplay-session") || crypto.randomUUID();
  localStorage.setItem("berlin-roleplay-session", session);
  axios.post(`${API}${path}`, { ...body, session_id: session }).catch(() => {});
}

function AdminShell({ title, subtitle, children }) {
  return (
    <section className="mx-auto min-h-[70vh] max-w-7xl px-4 py-10 sm:px-6">
      <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <div className="section-eyebrow">Berlin Roleplay System</div>
          <h1 className="section-title">{title}</h1>
          {subtitle && <p className="mt-2 max-w-3xl text-sm leading-6 text-zinc-500">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function AuthGate({ children, required = "admin" }) {
  const [token, setToken] = useState(getToken());
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!!token);
  const location = useLocation();

  const loadMe = useCallback(async () => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await adminGet("/auth/me");
      setUser(data);
    } catch (error) {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      setToken("");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe, token]);

  const startLogin = async () => {
    try {
      const data = await adminPost("/auth/fivem/start", { return_to: location.pathname });
      window.location.href = data.auth_url;
    } catch (error) {
      toast.error(normalizeError(error));
    }
  };

  if (loading) {
    return <AdminShell title="Login wird geprüft"><Card className="glass-card p-8 text-center"><Loader2 className="mx-auto h-8 w-8 animate-spin text-zinc-400" /></Card></AdminShell>;
  }

  if (!user) {
    return (
      <AdminShell title={required === "creator" ? "Creator Login" : "Admin Login"} subtitle="Melde dich mit deinem FiveM/Tebex Account an. Owner ist der Account HCP_Brucks; Creator werden im Adminbereich zugeordnet.">
        <Card className="glass-card mx-auto max-w-xl p-6 text-center" data-testid={`${required}-login-card`}>
          <Lock className="mx-auto h-10 w-10 text-zinc-500" />
          <h2 className="mt-5 font-display text-3xl tracking-[0.14em] text-white">Geschützter Bereich</h2>
          <p className="mt-3 text-sm leading-6 text-zinc-500">Die Anmeldung läuft über Tebex/FiveM. Es werden keine Admin-Secrets im Browser gespeichert oder angezeigt.</p>
          <Button onClick={startLogin} className="mt-6 rounded-full border border-white/20 bg-white/10 text-white hover:bg-white/15" data-testid={`${required}-fivem-login-button`}>
            Mit FiveM/Tebex anmelden <ExternalLink className="h-4 w-4" />
          </Button>
        </Card>
      </AdminShell>
    );
  }

  if (required === "creator" && !["creator", "owner", "admin"].includes(user.role)) {
    return <Forbidden />;
  }
  if (required === "admin" && !["owner", "admin", "support"].includes(user.role)) {
    return <Forbidden />;
  }

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken("");
    setUser(null);
    toast.success("Du wurdest ausgeloggt.");
  };

  return (
    <>
      <div className="mx-auto mt-4 flex max-w-7xl justify-end px-4 sm:px-6">
        <Button variant="secondary" onClick={logout} className="rounded-xl border border-white/10 bg-white/5 text-white hover:bg-white/10" data-testid="admin-logout-button">
          <LogOut className="h-4 w-4" /> Ausloggen
        </Button>
      </div>
      {children({ user, reloadUser: loadMe, logout })}
    </>
  );
}

function Forbidden() {
  return <AdminShell title="Kein Zugriff"><Card className="glass-card p-8 text-center"><Ban className="mx-auto h-10 w-10 text-red-300" /><p className="mt-4 text-zinc-400">Du hast keine Berechtigung für diesen Bereich.</p></Card></AdminShell>;
}

export function FiveMReturnPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [message, setMessage] = useState("FiveM Login wird abgeschlossen...");

  useEffect(() => {
    const run = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const basket = params.get("basket");
        const returnTo = params.get("returnTo") || "/admin";
        if (!basket) throw new Error("Basket fehlt.");
        const response = await axios.post(`${API}/auth/fivem/complete`, { basket_ident: basket });
        localStorage.setItem(ADMIN_TOKEN_KEY, response.data.data.token);
        setMessage("Login erfolgreich. Weiterleitung...");
        setTimeout(() => navigate(returnTo), 700);
      } catch (error) {
        setMessage(normalizeError(error));
      }
    };
    run();
  }, [location.search, navigate]);

  return <AdminShell title="FiveM Login"><Card className="glass-card p-8 text-center"><Shield className="mx-auto h-10 w-10 text-zinc-400" /><p className="mt-4 text-zinc-400" data-testid="fivem-return-message">{message}</p></Card></AdminShell>;
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <Card className="glass-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between"><div className="grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-white/5"><Icon className="h-5 w-5 text-zinc-400" /></div><span className="font-mono text-xs text-zinc-600">Live</span></div>
        <div className="mt-5 font-display text-4xl tracking-[0.12em] text-white">{value ?? 0}</div>
        <div className="mt-1 text-sm text-zinc-500">{label}</div>
        {sub && <div className="mt-3 text-xs text-zinc-600">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function AdminTabs({ active, onChange }) {
  const tabs = [
    ["overview", "Übersicht", BarChart3],
    ["analytics", "Analytics", Activity],
    ["transactions", "Transaktionen", ShoppingBag],
    ["customers", "Customers", Search],
    ["creators", "Creator", Users],
    ["messages", "Nachrichten", MessageSquare],
    ["blacklist", "Blacklist", Ban],
    ["website", "Website", Edit3],
    ["settings", "Settings", Settings],
  ];
  return <div className="mb-6 flex gap-2 overflow-x-auto rounded-2xl border border-white/5 bg-black/30 p-2">{tabs.map(([id, label, Icon]) => <button key={id} onClick={() => onChange(id)} className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm ${active === id ? "bg-white text-zinc-950" : "text-zinc-400 hover:bg-white/5 hover:text-white"}`} data-testid={`admin-tab-${id}`}><Icon className="h-4 w-4" />{label}</button>)}</div>;
}

export function AdminDashboardPage() {
  return <AuthGate required="admin">{({ user }) => <AdminDashboardInner user={user} />}</AuthGate>;
}

function AdminDashboardInner({ user }) {
  const [active, setActive] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDashboard(await adminGet("/admin/analytics/dashboard"));
    } catch (error) {
      toast.error(normalizeError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AdminShell title="Admin Dashboard" subtitle={`Eingeloggt als ${user.username} · Rolle: ${user.role}`}>
      <AdminTabs active={active} onChange={setActive} />
      {loading && <Card className="glass-card p-8"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></Card>}
      {!loading && active === "overview" && <Overview dashboard={dashboard} />}
      {!loading && active === "analytics" && <AnalyticsPanel />}
      {active === "transactions" && <TransactionsPanel />}
      {active === "customers" && <CustomerPanel />}
      {active === "creators" && <CreatorsPanel />}
      {active === "messages" && <MessagesPanel />}
      {active === "blacklist" && <BlacklistPanel />}
      {active === "website" && <WebsitePanel />}
      {active === "settings" && <SettingsPanel />}
    </AdminShell>
  );
}

function Overview({ dashboard }) {
  const d = dashboard || {};
  return (
    <div className="space-y-6" data-testid="admin-overview-panel">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Users} label="Gesamtbesucher" value={d.total_visitors} />
        <StatCard icon={Activity} label="Besucher heute" value={d.visitors_today} />
        <StatCard icon={BarChart3} label="Besucher 7 Tage" value={d.visitors_7_days} />
        <StatCard icon={ShoppingBag} label="Conversion Rate" value={`${d.conversion_rate || 0}%`} />
        <StatCard icon={Ticket} label="Produkt-Klicks" value={d.product_clicks} />
        <StatCard icon={Users} label="Aktive Creator" value={d.active_creators} />
        <StatCard icon={Ban} label="Blacklist Einträge" value={d.blacklist_entries} />
        <StatCard icon={ShoppingBag} label="Transaktionen" value={d.transactions} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <DataCard title="Meistgeklickte Produkte" rows={(d.top_products || []).map((p) => ({ a: p.product_name || p._id, b: `${p.clicks} Klicks`, c: p.category }))} />
        <DataCard title="Letzte Admin Aktivitäten" rows={(d.recent_audit || []).map((a) => ({ a: a.action, b: a.actor_username || "System", c: a.created_at }))} />
      </div>
    </div>
  );
}

function DataCard({ title, rows }) {
  return <Card className="glass-card"><CardHeader><CardTitle className="text-white">{title}</CardTitle></CardHeader><CardContent className="space-y-2">{rows.length === 0 && <p className="text-sm text-zinc-600">Noch keine Daten vorhanden.</p>}{rows.map((row, i) => <div key={i} className="grid grid-cols-[1fr_auto] gap-3 rounded-xl border border-white/5 bg-white/[0.03] p-3"><div><div className="truncate text-sm font-medium text-zinc-200">{row.a}</div><div className="truncate text-xs text-zinc-600">{row.c}</div></div><div className="font-mono text-xs text-zinc-400">{row.b}</div></div>)}</CardContent></Card>;
}

function AnalyticsPanel() {
  const [products, setProducts] = useState([]);
  const [visitors, setVisitors] = useState([]);
  useEffect(() => { adminGet("/admin/analytics/products").then(setProducts).catch((e) => toast.error(normalizeError(e))); adminGet("/admin/analytics/visitors").then(setVisitors).catch(() => {}); }, []);
  return <div className="grid gap-4 lg:grid-cols-2" data-testid="admin-analytics-panel"><DataCard title="Produkt Analytics" rows={products.map((p) => ({ a: p.product_name || p._id, b: `${p.clicks_total} Klicks`, c: `Heute ${p.clicks_today} · 7 Tage ${p.clicks_7_days}` }))} /><DataCard title="Meistbesuchte Seiten" rows={visitors.map((v) => ({ a: v.path, b: `${v.views} Views`, c: `${v.visitors} Besucher` }))} /></div>;
}

function TransactionsPanel() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const load = () => adminGet(`/admin/transactions${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(setRows).catch((e) => toast.error(normalizeError(e)));

  useEffect(load, []);
  return (
    <PanelWithSearch title="Transaktionen" q={q} setQ={setQ} onSearch={load} testid="admin-transactions-panel" placeholder="Transaktions-ID, Payment-ID, CFX-ID, FiveM Name, Discord, Creator Code...">
      <Table rows={rows} columns={["transaction_id", "payment_id", "customer_id", "cfx_id", "customer_name", "amount", "currency", "status", "creator_code"]} />
    </PanelWithSearch>
  );
}

function CustomerPanel() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const load = () => q && adminGet(`/admin/customers/search?q=${encodeURIComponent(q)}`).then(setRows).catch((e) => toast.error(normalizeError(e)));
  return (
    <PanelWithSearch title="Customer Lookup" q={q} setQ={setQ} onSearch={load} testid="admin-customers-panel" placeholder="CFX-ID, Customer-ID, Transaktions-ID, FiveM Name, Discord oder E-Mail...">
      <Table rows={rows} columns={["transaction_id", "payment_id", "customer_id", "cfx_id", "customer_name", "fivem_username", "customer_email", "discord_id", "amount"]} />
    </PanelWithSearch>
  );
}

function PanelWithSearch({ title, q, setQ, onSearch, children, testid, placeholder = "Suchen..." }) {
  return <Card className="glass-card" data-testid={testid}><CardHeader><CardTitle className="text-white">{title}</CardTitle></CardHeader><CardContent><div className="mb-4 flex gap-2"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} className="border-white/10 bg-black/40" /><Button onClick={onSearch}><Search className="h-4 w-4" /> Suche</Button></div>{children}</CardContent></Card>;
}

function Table({ rows, columns }) {
  return <div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead><tr className="border-b border-white/5 text-zinc-500">{columns.map((c) => <th key={c} className="p-2 font-medium">{c}</th>)}</tr></thead><tbody>{rows.length === 0 && <tr><td className="p-4 text-zinc-600" colSpan={columns.length}>Keine Daten vorhanden.</td></tr>}{rows.map((row, i) => <tr key={row.id || i} className="border-b border-white/5 text-zinc-300">{columns.map((c) => <td key={c} className="max-w-[220px] truncate p-2">{String(row[c] ?? "-")}</td>)}</tr>)}</tbody></table></div>;
}

function CreatorsPanel() {
  const empty = { wallet_reference: "", fivem_username: "", code: "", commission_percent: 1, status: "active" };
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => adminGet("/admin/creators").then(setRows).catch((e) => toast.error(normalizeError(e)));
  useEffect(load, []);

  const reset = () => {
    setForm(empty);
    setEditId(null);
  };

  const save = async () => {
    try {
      const payload = {
        wallet_reference: form.wallet_reference,
        fivem_username: form.fivem_username,
        cfx_username: form.fivem_username,
        name: form.fivem_username,
        code: form.code || null,
        commission_percent: Number(form.commission_percent || 0),
        status: form.status || "active",
      };

      if (editId) {
        await adminPatch(`/admin/creators/${editId}`, payload);
        toast.success("Creator Link bearbeitet");
      } else {
        await adminPost("/admin/creators", payload);
        toast.success("Creator Link angelegt");
      }

      reset();
      load();
    } catch (e) {
      toast.error(normalizeError(e));
    }
  };

  const edit = (creator) => {
    setEditId(creator.id);
    setForm({
      wallet_reference: creator.wallet_reference || "",
      fivem_username: creator.fivem_username || creator.cfx_username || "",
      code: creator.code || creator.tebex_creator_code || "",
      commission_percent: creator.commission_percent || 1,
      status: creator.status || "active",
    });
  };

  const sync = async (creator) => {
    try {
      await adminPost(`/admin/creators/${creator.id}/sync-tebex-code`, {});
      toast.success("Tebex Creator Code synchronisiert");
      load();
    } catch (e) {
      toast.error(normalizeError(e));
    }
  };

  const del = async (id) => {
    await adminDelete(`/admin/creators/${id}`);
    toast.success("Creator Link gelöscht");
    load();
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[0.8fr_1.2fr]" data-testid="admin-creators-panel">
      <Card className="glass-card">
        <CardHeader><CardTitle>{editId ? "Creator Link bearbeiten" : "Creator Link anlegen"}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm leading-6 text-zinc-500">Hier verknüpfst du die Tebex Wallet Reference mit dem FiveM/CFX Namen. Der Creator meldet sich danach mit FiveM an und sieht seinen Creator Code.</p>
          <Input placeholder="Tebex Wallet Reference z.B. centralised_245597_938" value={form.wallet_reference} onChange={(e) => setForm({ ...form, wallet_reference: e.target.value })} />
          <Input placeholder="FiveM / CFX Username z.B. HCP_Brucks" value={form.fivem_username} onChange={(e) => setForm({ ...form, fivem_username: e.target.value })} />
          <Input placeholder="Tebex Creator Code optional, z.B. 123" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Input placeholder="Creator Anteil in %" type="number" value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: Number(e.target.value) })} />
          <Input placeholder="Status active/inactive" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} />
          <div className="flex gap-2">
            <Button onClick={save} data-testid="admin-create-creator-button">{editId ? "Änderungen speichern" : "Creator Link speichern"}</Button>
            {editId && <Button variant="secondary" onClick={reset}>Abbrechen</Button>}
          </div>
        </CardContent>
      </Card>
      <Card className="glass-card">
        <CardHeader><CardTitle>Creator Verwaltung</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {rows.map((c) => (
            <div key={c.id} className="rounded-xl border border-white/5 bg-white/[0.03] p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-white">{c.fivem_username || c.cfx_username || c.name || "Creator"} <Badge>{c.code || c.tebex_creator_code || "kein Code"}</Badge></div>
                  <div className="text-xs text-zinc-600">Wallet: {c.wallet_reference || "-"} · FiveM: {c.fivem_username || c.cfx_username || "-"} · Status: {c.status}</div>
                  <div className="mt-1 text-xs text-zinc-500">Einlösungen: {c.synced_redemptions ?? c.redemptions ?? 0} · Umsatz: {(c.synced_revenue ?? c.revenue ?? 0).toFixed(2)} € · Creator Anteil: {(c.commission_amount || 0).toFixed(2)} €</div>
                  {c.last_tebex_sync && <div className="mt-1 text-[11px] text-zinc-600">Letzter Tebex Sync: {c.last_tebex_sync}</div>}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button variant="secondary" onClick={() => edit(c)}>Bearbeiten</Button>
                  <Button variant="secondary" onClick={() => sync(c)}>Tebex Code sync</Button>
                  <Button variant="ghost" onClick={() => del(c.id)}>Löschen</Button>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function MessagesPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { adminGet("/admin/creator-messages").then(setRows).catch((e) => toast.error(normalizeError(e))); }, []);
  return <Card className="glass-card" data-testid="admin-messages-panel"><CardHeader><CardTitle>Creator Nachrichten</CardTitle></CardHeader><CardContent><Table rows={rows} columns={["creator_name", "channel", "title", "status", "day", "created_at"]} /></CardContent></Card>;
}

function BlacklistPanel() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ discord_id: "", username: "", fivem_username: "", ip: "", customer_id: "", cfx_id: "", reason: "" });

  const load = () => adminGet(`/admin/blacklist${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(setRows).catch((e) => toast.error(normalizeError(e)));
  useEffect(load, []);

  const create = async () => {
    try {
      await adminPost("/admin/blacklist", form);
      toast.success("Blacklist Eintrag erstellt");
      setForm({ discord_id: "", username: "", fivem_username: "", ip: "", customer_id: "", cfx_id: "", reason: "" });
      load();
    } catch (e) {
      toast.error(normalizeError(e));
    }
  };

  const remove = async (id) => {
    try {
      await adminDelete(`/admin/blacklist/${id}`);
      toast.success("Blacklist Eintrag entfernt");
      load();
    } catch (e) {
      toast.error(normalizeError(e));
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[0.75fr_1.25fr]" data-testid="admin-blacklist-panel">
      <Card className="glass-card">
        <CardHeader><CardTitle>Blacklist hinzufügen</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Input placeholder="Discord ID" value={form.discord_id} onChange={(e) => setForm({ ...form, discord_id: e.target.value })} />
          <Input placeholder="FiveM Name / Username" value={form.fivem_username} onChange={(e) => setForm({ ...form, fivem_username: e.target.value, username: e.target.value })} />
          <Input placeholder="CFX-ID / Customer-ID" value={form.cfx_id} onChange={(e) => setForm({ ...form, cfx_id: e.target.value, customer_id: e.target.value })} />
          <Input placeholder="IP-Adresse" value={form.ip} onChange={(e) => setForm({ ...form, ip: e.target.value })} />
          <Textarea placeholder="Grund" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          <Button onClick={create}>Speichern</Button>
        </CardContent>
      </Card>
      <Card className="glass-card">
        <CardHeader><CardTitle>Aktuelle Einträge</CardTitle></CardHeader>
        <CardContent>
          <div className="mb-4 flex gap-2">
            <Input placeholder="Blacklist durchsuchen..." value={q} onChange={(e) => setQ(e.target.value)} className="border-white/10 bg-black/40" />
            <Button onClick={load}><Search className="h-4 w-4" /> Suche</Button>
          </div>
          <div className="space-y-2">
            {rows.length === 0 && <p className="text-sm text-zinc-600">Keine Einträge vorhanden.</p>}
            {rows.map((row) => (
              <div key={row.id} className="rounded-xl border border-white/5 bg-white/[0.03] p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-white">{row.fivem_username || row.username || row.discord_id || row.customer_id || row.cfx_id || "Blacklist Eintrag"}</div>
                    <div className="text-xs text-zinc-600">Discord: {row.discord_id || "-"} · CFX: {row.cfx_id || row.customer_id || "-"} · IP: {row.ip_hash ? "gespeichert" : "-"}</div>
                    <div className="text-xs text-zinc-500">Grund: {row.reason || "-"} · Status: {row.status || "-"}</div>
                  </div>
                  <Button variant="ghost" onClick={() => remove(row.id)} data-testid={`blacklist-delete-${row.id}`}>Entfernen</Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function WebsitePanel() {
  const [values, setValues] = useState({});
  useEffect(() => { adminGet("/admin/site-content").then(setValues).catch((e) => toast.error(normalizeError(e))); }, []);
  const save = async () => { try { const data = await adminPatch("/admin/site-content", { values }); setValues(data); toast.success("Website Inhalte gespeichert"); } catch (e) { toast.error(normalizeError(e)); } };
  return <Card className="glass-card" data-testid="admin-website-panel"><CardHeader><CardTitle>Website bearbeiten</CardTitle></CardHeader><CardContent className="grid gap-3 md:grid-cols-2">{["hero_title", "hero_description", "discord_link", "hero_image", "logo_text", "cta_primary", "cta_secondary", "footer_text", "support_text", "legal_refund", "legal_returns"].map((key) => <label key={key} className="space-y-1"><span className="font-mono text-xs text-zinc-500">{key}</span><Textarea value={values[key] || ""} onChange={(e) => setValues({ ...values, [key]: e.target.value })} className="border-white/10 bg-black/40" /></label>)}<div className="md:col-span-2"><Button onClick={save}>Änderungen speichern</Button></div></CardContent></Card>;
}

function SettingsPanel() {
  const [settings, setSettings] = useState([]);
  const [values, setValues] = useState({});
  const load = () => adminGet("/admin/settings").then(setSettings).catch((e) => toast.error(normalizeError(e)));
  useEffect(load, []);
  const save = async () => { try { await adminPatch("/admin/settings", { values }); setValues({}); toast.success("Secrets serverseitig gespeichert"); load(); } catch (e) { toast.error(normalizeError(e)); } };
  return <Card className="glass-card" data-testid="admin-settings-panel"><CardHeader><CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> API & Secret Einstellungen</CardTitle></CardHeader><CardContent className="space-y-4"><p className="text-sm text-zinc-500">Werte werden nur serverseitig gespeichert. API-Antworten zeigen niemals den vollständigen Secret-Wert.</p>{settings.map((s) => <div key={s.key} className="grid gap-2 rounded-xl border border-white/5 bg-white/[0.03] p-3 md:grid-cols-[1fr_1fr_auto]"><div><div className="font-mono text-xs text-zinc-400">{s.key}</div><div className="text-xs text-zinc-600">{s.configured ? `Konfiguriert (${s.masked})` : "Nicht konfiguriert"} · {s.source}</div></div><Input type="password" placeholder="Neuen Wert eintragen" value={values[s.key] || ""} onChange={(e) => setValues({ ...values, [s.key]: e.target.value })} /><Badge className={s.configured ? "bg-emerald-500/10 text-emerald-100" : "bg-red-500/10 text-red-100"}>{s.configured ? "OK" : "Fehlt"}</Badge></div>)}<Button onClick={save}>Settings speichern</Button></CardContent></Card>;
}

export function CreatorDashboardPage() {
  return <AuthGate required="creator">{() => <CreatorDashboardInner />}</AuthGate>;
}

function CreatorDashboardInner() {
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [limits, setLimits] = useState(null);
  const [message, setMessage] = useState({ title: "", description: "", image_url: "", button_link: "" });
  const load = useCallback(async () => { try { setMe(await adminGet("/creator/me")); setStats(await adminGet("/creator/stats")); setLimits(await adminGet("/creator/message/limits")); } catch (e) { toast.error(normalizeError(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  const send = async (channel) => { try { await adminPost(`/creator/message/${channel}`, message); toast.success(channel === "discord" ? "Discord Embed gesendet" : "Ingame Nachricht in Queue gestellt"); setMessage({ title: "", description: "", image_url: "", button_link: "" }); load(); } catch (e) { toast.error(normalizeError(e)); } };
  return <AdminShell title="Creator Dashboard" subtitle="Deine Creator-Statistiken, Code-Nutzung und tägliche Nachrichten."><div className="grid gap-4 md:grid-cols-4"><StatCard icon={Ticket} label="Creator Code" value={me?.code || me?.tebex_creator_code || "-"} sub={me?.wallet_reference ? `Wallet: ${me.wallet_reference}` : ""} /><StatCard icon={ShoppingBag} label="Käufe" value={stats?.purchases || 0} /><StatCard icon={BarChart3} label="Einlösungen" value={stats?.redemptions || 0} /><StatCard icon={CheckCircle2} label="Umsatz" value={`${(stats?.estimated_revenue || 0).toFixed(2)} €`} sub={`Dein Anteil: ${(stats?.commission_amount || 0).toFixed(2)} €`} /></div><div className="mt-6 grid gap-4 lg:grid-cols-[0.8fr_1.2fr]"><Card className="glass-card" data-testid="creator-profile-card"><CardHeader><CardTitle>{me?.name || "Creator"}</CardTitle></CardHeader><CardContent className="space-y-2 text-sm text-zinc-500"><p>FiveM: {me?.fivem_username || me?.cfx_username || "-"}</p><p>Wallet Reference: {me?.wallet_reference || "-"}</p><p>Tebex Creator Code: {me?.code || me?.tebex_creator_code || "-"}</p><p>Discord: {me?.discord_name || me?.discord_id || "-"}</p><p>Status: {me?.status || "-"}</p><p>Discord Nachricht heute: {limits?.discord_available ? "verfügbar" : "bereits genutzt"}</p><p>Ingame Nachricht heute: {limits?.ingame_available ? "verfügbar" : "bereits genutzt"}</p></CardContent></Card><Card className="glass-card" data-testid="creator-message-card"><CardHeader><CardTitle>Creator Nachricht senden</CardTitle></CardHeader><CardContent className="space-y-3"><Input placeholder="Titel" value={message.title} onChange={(e) => setMessage({ ...message, title: e.target.value })} /><Textarea placeholder="Beschreibung" value={message.description} onChange={(e) => setMessage({ ...message, description: e.target.value })} /><Input placeholder="Bild URL (optional)" value={message.image_url} onChange={(e) => setMessage({ ...message, image_url: e.target.value })} /><Input placeholder="Stream Link (optional)" value={message.button_link} onChange={(e) => setMessage({ ...message, button_link: e.target.value })} /><div className="flex flex-wrap gap-2"><Button onClick={() => send("discord")} disabled={!limits?.discord_available} data-testid="creator-send-discord-button"><Send className="h-4 w-4" /> Discord Embed</Button><Button onClick={() => send("ingame")} disabled={!limits?.ingame_available} variant="secondary" data-testid="creator-send-ingame-button"><MessageSquare className="h-4 w-4" /> Ingame Queue</Button></div></CardContent></Card></div></AdminShell>;
}

export function BlacklistedPage({ status }) {
  return <section className="mx-auto grid min-h-[70vh] max-w-3xl place-items-center px-4"><Card className="glass-card p-8 text-center" data-testid="blacklisted-page"><XCircle className="mx-auto h-12 w-12 text-red-300" /><h1 className="mt-5 font-display text-4xl tracking-[0.14em] text-white">Du wurdest vom Berlin Roleplay Shop ausgeschlossen.</h1><p className="mt-4 text-zinc-500">{status?.reason || "Bitte öffne bei Fragen ein Support-Ticket auf Discord."}</p>{status?.expiresAt && <p className="mt-2 font-mono text-xs text-zinc-600">Ablaufdatum: {status.expiresAt}</p>}</Card></section>;
}
